import { DateTime } from 'luxon';
import { action, makeObservable, observable } from 'mobx';
import dayjs, { Dayjs } from 'dayjs';

import { ExternalLibraryFilesFilterInput } from '@clipr/lib';

import { ApiVariables } from '../../api/apiSchema';
import { dateInput, DateInputState } from '../../components/input/dateInput/dateInputState';
import { Nullable } from '../../core';
import { BindingProps } from '../../store';
import { Store } from '../../store/store';
import { LibraryCatalogSource } from './libraryCatalogSource';

type Props = BindingProps<{
  pageSize?: number,
}>

export class ZoomCatalogSource
  extends LibraryCatalogSource {

  readonly nodeType: 'ZoomCatalogSource' = 'ZoomCatalogSource';
  readonly datepicker: DateInputState;
  constructor(store: Store, props?: Props) {
    super(store, { library: store.zoom, ...props });
    makeObservable(this);

    this.datepicker = dateInput(this, {
      variant: 'outlined',
      placeholder: ['dd/mm/yyyy', 'dd/mm/yyyy'],
      shouldDisableDates: true,
      validationMessage: 'Maximum allowed range is one month',
      format: ['DD/MM/YYYY', 'DDMMYYYY'],
      activeRangeOption: {
        name: 'Last 30 Days',
        getRange: () => {
          return [dayjs().subtract(31, 'day'), dayjs()];
        }
      },
      rangeOptions: [
        {
          name: 'Last 30 Days',
          getRange: () => {
            return [dayjs().subtract(31, 'day'), dayjs()];
          }
        },
        {
          name: 'Yesterday',
          getRange: () => {
            return [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')];
          }
        },
        {
          name: 'Today',
          getRange: () => {
            return [dayjs(), dayjs()];
          }
        }
      ]
    });
  }

  @observable startDate: Nullable<string> = DateTime.local().minus({ month: 1 }).toISODate();
  @observable endDate: Nullable<string> = DateTime.local().toISODate();

  @action
  handleDateChange() {
    const value = this.datepicker.value as [Dayjs, Dayjs];
    const startDateValue = (value && value[0]) || DateTime.local().minus({ month: 1 }).toISODate();
    const endDateValue = (value && value[1]) || DateTime.local().toISODate();
    this.startDate = (dayjs(startDateValue).format('YYYY-MM-DD').toString()) || null;
    this.endDate = (dayjs(endDateValue).format('YYYY-MM-DD').toString()) || null;
    this.fetch();
  }

  // #region Overwritten class methods
  // -------
  getFetchArgs(more: boolean = false) {
    const value = this.datepicker.value as [Dayjs, Dayjs];
    const startDateValue = (value && value[0]) || DateTime.local().minus({ month: 1 }).toISODate();
    const endDateValue = (value && value[1]) || DateTime.local().toISODate();
    this.startDate = (dayjs(startDateValue).format('YYYY-MM-DD').toString()) || null;
    this.endDate = (dayjs(endDateValue).format('YYYY-MM-DD').toString()) || null;

    const args: ApiVariables<'getExternalLibrary'> = {
      id: this.store.zoom.library?.id
    };

    const filter: ExternalLibraryFilesFilterInput = {
      startDate: this.startDate as string,
      endDate: this.endDate as string
    };

    if (this.sortField && this.sortOrder) {
      args.sort = {
        field: this.sortField,
        order: this.sortOrder
      }
    }

    if (Object.keys(filter).length > 0) {
      args.filter = filter;
    }

    args.first = this.pageSize;

    if (more && this.lastItemCursor) {
      args.after = this.lastItemCursor;
    }

    return args;
  }

  reset() {
    this.endDate = DateTime.local().toISODate();
    this.startDate = DateTime.local().minus({ month: 1 }).toISODate();
    this.clearMultipleSelection();
    this.fetch();
  }
  // #endregion
}

